import React, { useState, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Stack, 
  Avatar, useMediaQuery, Icon
} from '@mui/material';
import { ScheduleOutlined as ScheduleIcon, PersonAddOutlined as VolunteerIcon } from '@mui/icons-material';
import { useTheme } from '@emotion/react';
import { useFullHeightPageFix } from 'src/utils/useFullHeightPageFix.js';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import MaestroNavbar from './blocks/MaestroNavbar.js';
import MaestroSideTray from './blocks/MaestroSideTray.js';
import TrayToggleButton from './blocks/TrayToggleButton.js';

const ActionCard = ({ title, subtitle, onClick, children, variant = "outlined" }) => {
  const theme = useTheme();
  
  return (
    <Card 
      variant="outlined"
      sx={{ 
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        borderRadius: "20px",
        boxShadow: theme.shadows.cardLg ? theme.shadows.cardLg(theme.palette, theme.palette.mode) : theme.shadows[8],
        backgroundColor: theme.palette.background.paper,
        border: "none",
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows.cardLg ? theme.shadows.cardLg(theme.palette, theme.palette.mode) : theme.shadows[12]
        }
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {subtitle}
          </Typography>
        )}
        {children}
      </CardContent>
    </Card>
  );
};

const CommunityPage = () => {
  const containerRef = useRef(null);
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sideTrayOpen, setSideTrayOpen] = useState(!isMobile);
  
  useFullHeightPageFix(containerRef);

  const handleScheduleMentor = () => {
    // TODO: Implement mentor scheduling
    console.log('Schedule with mentor clicked');
  };

  const handleUpdateAgenda = () => {
    // TODO: Implement agenda update
    console.log('Update agenda clicked');
  };

  const handleRequestVolunteer = () => {
    // TODO: Implement volunteer request
    console.log('Request volunteer clicked');
  };

  const handleNewChat = () => {
    navigate('/chat');
  };

  const toggleSideTray = () => {
    setSideTrayOpen(!sideTrayOpen);
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <Helmet>
        <title>Community - Teaching Support</title>
        <meta name="description" content="Connect with mentors and volunteers for teaching support." />
      </Helmet>
      
      {/* Side Tray */}
      <MaestroSideTray 
        open={sideTrayOpen}
        onClose={() => setSideTrayOpen(false)}
        onNewChat={handleNewChat}
      />
      
      {/* Toggle Button (only visible when tray is closed or on mobile) */}
      {(!sideTrayOpen || isMobile) && (
        <TrayToggleButton 
          onClick={toggleSideTray}
          open={sideTrayOpen}
        />
      )}

      {/* Main Content */}
      <Box 
        ref={containerRef} 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          height: '100vh', 
          overflow: 'auto',
          flexGrow: 1,
          width: sideTrayOpen && !isMobile ? 'calc(100% - 300px)' : '100%',
          transition: theme.transitions.create(['margin-left', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          alignItems: 'center',
          justifyContent: 'flex-start'
        }}
      >
        <MaestroNavbar />
      
        <Box 
          display="flex" 
          flexDirection="column" 
          sx={{ 
            width: "100%",
            maxWidth: { xs: "100%", sm: "600px" }, 
            p: 3,
            my: 2,
            borderRadius: "20px",
            boxShadow: theme.shadows.cardLg ? theme.shadows.cardLg(theme.palette, theme.palette.mode) : theme.shadows[8],
            backgroundColor: theme.palette.background.paper
          }}
        >
          <Typography
            variant="h4"
            sx={{ mb: 1, mt: { md: 3 }, fontWeight: "600" }}
          >
            Community
          </Typography>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 4 }}>
            Connect with mentors and volunteers for teaching support.
          </Typography>

          {/* Action Cards */}
          <Stack spacing={3}>
            {/* Schedule with Mentor */}
            <ActionCard 
              title="Schedule with Mentor →"
              subtitle="Book time with your teaching mentor for guidance and support"
              onClick={handleScheduleMentor}
            />

            {/* Upcoming Mentor Meeting */}
            <Card 
              variant="outlined"
              sx={{
                borderRadius: "20px",
                boxShadow: theme.shadows.cardLg ? theme.shadows.cardLg(theme.palette, theme.palette.mode) : theme.shadows[8],
                backgroundColor: theme.palette.background.paper,
                border: "none"
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <ScheduleIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, color: 'text.secondary', mb: 0 }}>
                    NEXT SCHEDULED MEETING
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                      August 25th Meeting with Mentor
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      2:00 PM - 3:00 PM
                    </Typography>
                  </Box>
                  
                  <Button 
                    variant="outlined" 
                    size="small"
                    onClick={handleUpdateAgenda}
                  >
                    Update Agenda
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Request a Volunteer */}
            <ActionCard 
              title="Request a Volunteer →"
              subtitle="Get additional support from our community of volunteer educators"
              onClick={handleRequestVolunteer}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                <VolunteerIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  Connect with experienced teachers who can provide specialized help
                </Typography>
              </Box>
            </ActionCard>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default CommunityPage;